import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createCn } from '../../../helpers/utils';
import {
  MeetingInsights,
  PresentationDeck,
  SlideDeckSummary,
  SlideTimelineEntry,
  SpeechSegment,
  apiService,
} from '../../../services/api.service';
import {
  formatSecondsShort,
  formatSpeakerFullName,
  stripSpeakerOrgSuffix,
} from '../utils/audio-segments';
import { langLabel } from '../utils/speaker-language';
import { attachSlideThumbnails, buildVideoMoments } from '../utils/video-moments.utils';
import { buildSlideSections } from '../utils/slide-panel.utils';
import MeetingAudioPlayer from './meeting-audio-player.component';
import PlayerSlidePanel from './video-analytics/player-slide-panel.component';
import { SpeakerTimelineItem } from './meeting-analytics-tab.component';
import classes from '../styles.module.scss';

const cn = createCn('file', classes);

type SlideTrack = {
  id: string;
  label: string;
  startSec: number;
  endSec: number;
  startPct: number;
  widthPct: number;
};

type Props = {
  meetingId: string;
  isVideoMeeting: boolean;
  audioUrl: string;
  videoUrl: string;
  segments: SpeechSegment[];
  speakerTimelines: SpeakerTimelineItem[];
  durationSec: number;
  currentTime: number;
  soloSpeakerName?: string | null;
  insights: MeetingInsights | null;
  slideTimeline: SlideTimelineEntry[];
  presentationSlides: SlideTimelineEntry[];
  presentationDecks: PresentationDeck[];
  slideDeckSummary: SlideDeckSummary | null;
  getFrameUrl: (meetingId: string, index: number, crop?: boolean) => string;
  onExitSolo?: () => void;
  onTimeChange: (time: number) => void;
  onDurationChange: (duration: number) => void;
  onPlayingChange?: (playing: boolean) => void;
  onSeek: (seconds: number) => void;
  onSpeakerPlay?: (speakerName: string) => void;
  onVideoAttached?: () => void;
  slidesAnalyzing?: boolean;
  onAnalyzeSlides?: () => void;
  seekRef?: React.MutableRefObject<((time: number) => void) | null>;
  togglePlayPauseRef?: React.MutableRefObject<(() => void) | null>;
};

function parseSlideTimeSec(entry: SlideTimelineEntry): number {
  if (typeof entry.time_sec === 'number' && Number.isFinite(entry.time_sec)) {
    return entry.time_sec;
  }
  const ts = (entry.timestamp || '').trim();
  const parts = ts.split(':').map(Number);
  if (parts.length === 3 && parts.every(n => Number.isFinite(n))) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  if (parts.length === 2 && parts.every(n => Number.isFinite(n))) {
    return parts[0] * 60 + parts[1];
  }
  return 0;
}

function buildSlideTracks(
  slides: SlideTimelineEntry[],
  durationSec: number,
): SlideTrack[] {
  const sorted = [...slides]
    .map((s, i) => ({ s, i, start: parseSlideTimeSec(s) }))
    .filter(x => x.start >= 0)
    .sort((a, b) => a.start - b.start || a.i - b.i);

  if (!sorted.length || durationSec <= 0) return [];

  return sorted.map((item, idx) => {
    const next = sorted[idx + 1];
    const endSec = next ? next.start : durationSec;
    const startPct = (item.start / durationSec) * 100;
    const widthPct = Math.max(((endSec - item.start) / durationSec) * 100, 0.4);
    const title = (item.s.title || '').trim();
    const label = title
      ? (title.length > 42 ? `${title.slice(0, 40)}…` : title)
      : `Сцена ${(item.s.slide_index ?? idx) + 1}`;
    return {
      id: `slide-${item.i}-${item.start}`,
      label,
      startSec: item.start,
      endSec,
      startPct,
      widthPct,
    };
  });
}

const TranscriptionMediaPanel: React.FC<Props> = ({
  meetingId,
  isVideoMeeting,
  audioUrl,
  videoUrl,
  segments,
  speakerTimelines,
  durationSec,
  currentTime,
  soloSpeakerName = null,
  insights,
  slideTimeline,
  presentationSlides,
  presentationDecks,
  slideDeckSummary,
  getFrameUrl,
  onExitSolo,
  onTimeChange,
  onDurationChange,
  onPlayingChange,
  onSeek,
  onSpeakerPlay,
  onVideoAttached,
  slidesAnalyzing = false,
  onAnalyzeSlides,
  seekRef,
  togglePlayPauseRef,
}) => {
  const audioSeekRef = useRef<((time: number) => void) | null>(null);
  const videoSeekRef = useRef<((time: number) => void) | null>(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadError, setVideoUploadError] = useState('');

  const seekAll = useCallback((time: number) => {
    onTimeChange(time);
    audioSeekRef.current?.(time);
    videoSeekRef.current?.(time);
  }, [onTimeChange]);

  useEffect(() => {
    if (!seekRef) return;
    seekRef.current = seekAll;
    return () => {
      seekRef.current = null;
    };
  }, [seekRef, seekAll]);

  const moments = useMemo(() => {
    const base = buildVideoMoments(insights);
    return attachSlideThumbnails(base, slideTimeline);
  }, [insights, slideTimeline]);

  const slideSections = useMemo(
    () => buildSlideSections(
      presentationDecks,
      presentationSlides,
      slideTimeline,
      slideDeckSummary,
      moments,
    ),
    [presentationDecks, presentationSlides, slideTimeline, slideDeckSummary, moments],
  );

  const slideTracks = useMemo(
    () => buildSlideTracks(slideTimeline, durationSec),
    [slideTimeline, durationSec],
  );

  const getThumbUrl = useCallback(
    (slideIndex: number) => getFrameUrl(meetingId, slideIndex, true),
    [getFrameUrl, meetingId],
  );

  const showAudio = !!audioUrl;
  const showVideoPlayer = !!videoUrl;

  const handleVideoFile = useCallback(async (file: File | undefined) => {
    if (!file || !meetingId) return;
    setVideoUploadError('');
    setVideoUploading(true);
    try {
      await apiService.attachMeetingVideo(meetingId, file);
      onVideoAttached?.();
    } catch (e) {
      setVideoUploadError(e instanceof Error ? e.message : 'Ошибка загрузки видео');
    } finally {
      setVideoUploading(false);
    }
  }, [meetingId, onVideoAttached]);

  if (!showAudio && !showVideoPlayer && !meetingId) return null;

  return (
    <div className={cn('transcript-player')}>
      {showAudio && (
        <section className={cn('transcript-media-section')}>
          <div className={cn('transcript-media-head')}>
            <span className={cn('transcript-media-label')}>Аудио</span>
            <span className={cn('transcript-media-hint')}>волна и таймкоды реплик</span>
          </div>
          <MeetingAudioPlayer
            audioUrl={audioUrl}
            meetingId={meetingId}
            segments={segments}
            speakerTimelines={speakerTimelines}
            durationSec={durationSec}
            soloSpeakerName={soloSpeakerName}
            onExitSolo={onExitSolo}
            onTimeChange={onTimeChange}
            onDurationChange={onDurationChange}
            seekRef={audioSeekRef}
            togglePlayPauseRef={togglePlayPauseRef}
          />
        </section>
      )}

      <section className={cn('transcript-media-section', { video: true })}>
        <div className={cn('transcript-media-head')}>
          <span className={cn('transcript-media-label')}>Видео</span>
          <span className={cn('transcript-media-badge', { teams: true })}>Microsoft Teams · Zoom</span>
          <span className={cn('transcript-media-hint')}>
            {showVideoPlayer ? 'запись совещания + панель контента' : 'прикрепите .mp4 из Teams или Zoom'}
          </span>
        </div>

        {showVideoPlayer ? (
          <>
            <div className={cn('transcript-teams-player')}>
              <PlayerSlidePanel
                videoUrl={videoUrl}
                durationSec={durationSec}
                currentTime={currentTime}
                markers={moments}
                sections={slideSections}
                getThumbUrl={getThumbUrl}
                onTimeChange={onTimeChange}
                onDurationChange={onDurationChange}
                onPlayingChange={onPlayingChange}
                onSeek={seekAll}
                seekRef={videoSeekRef}
                togglePlayPauseRef={togglePlayPauseRef}
                slidesAnalyzing={slidesAnalyzing}
                onAnalyzeSlides={onAnalyzeSlides}
              />
            </div>

            {slideTracks.length > 0 && (
              <div className={cn('video-tracks')}>
                <div className={cn('video-tracks-title')}>
                  <span>Видеодорожки</span>
                  <span className={cn('video-tracks-hint')}>
                    {` · ${slideTracks.length} сцен · клик — перемотка`}
                  </span>
                </div>
                <div className={cn('video-tracks-lane')}>
                  {slideTracks.map(track => {
                    const isActive = currentTime >= track.startSec && currentTime < track.endSec;
                    return (
                      <button
                        key={track.id}
                        type="button"
                        className={cn('video-tracks-segment', { active: isActive })}
                        style={{
                          left: `${track.startPct}%`,
                          width: `${track.widthPct}%`,
                        }}
                        title={`${formatSecondsShort(track.startSec)} · ${track.label}`}
                        onClick={() => seekAll(track.startSec)}
                      >
                        <span className={cn('video-tracks-segment-label')}>{track.label}</span>
                      </button>
                    );
                  })}
                  <div
                    className={cn('video-tracks-playhead')}
                    style={{
                      left: durationSec > 0
                        ? `${Math.min(100, (currentTime / durationSec) * 100)}%`
                        : '0%',
                    }}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={cn('transcript-video-upload')}>
            <div className={cn('transcript-video-mock')}>
              <div className={cn('transcript-video-mock-player')}>
                <span className={cn('transcript-video-mock-icon')}>▶</span>
                <span>Видео совещания</span>
              </div>
              <div className={cn('transcript-video-mock-slides')}>
                <span className={cn('transcript-video-mock-slides-title')}>Слайды и экран</span>
                <span className={cn('transcript-video-mock-slides-hint')}>
                  Панель контента появится после загрузки .mp4
                </span>
              </div>
            </div>
            <p className={cn('transcript-video-upload-text')}>
              {isVideoMeeting
                ? 'Видеозапись не прикреплена. Загрузите файл, который вы скачали из Teams или Zoom (.mp4).'
                : 'К этой встрече загружен только звук (.m4a / .mp3). Чтобы увидеть плеер Teams/Zoom, прикрепите видеозапись того же совещания.'}
            </p>
            <label className={cn('transcript-video-upload-btn')}>
              {videoUploading ? 'Загрузка…' : 'Добавить видео Teams / Zoom'}
              <input
                type="file"
                accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.mkv,.avi,.m4v"
                disabled={videoUploading}
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  void handleVideoFile(file);
                  e.target.value = '';
                }}
              />
            </label>
            {videoUploadError && (
              <p className={cn('transcript-video-upload-error')}>{videoUploadError}</p>
            )}
          </div>
        )}
      </section>

      {showAudio && speakerTimelines.length > 0 && (
        <section className={cn('transcript-media-section', { tracks: true })}>
          <div className={cn('audio-tracks')}>
            <div className={cn('audio-tracks-title')}>
              <span>Дорожки спикеров</span>
              <span className={cn('audio-tracks-hint')}> · клик — solo и перемотка</span>
            </div>
            <div className={cn('audio-speakers')}>
              {speakerTimelines.map(speaker => {
                const isSolo = soloSpeakerName != null && (
                  soloSpeakerName === speaker.name
                  || speaker.name.toLowerCase().includes(soloSpeakerName.toLowerCase())
                );
                return (
                  <div key={speaker.id} className={cn('audio-speaker', { solo: isSolo })}>
                    <div
                      className={cn('audio-speaker-label')}
                      role="button"
                      tabIndex={0}
                      title="Клик — воспроизвести только этого спикера"
                      onClick={() => onSpeakerPlay?.(speaker.name)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onSpeakerPlay?.(speaker.name);
                        }
                      }}
                    >
                      <span
                        className={cn('audio-speaker-dot')}
                        style={{ backgroundColor: speaker.color }}
                      />
                      <span
                        className={cn('audio-speaker-name')}
                        style={{ color: speaker.color }}
                        title={stripSpeakerOrgSuffix(speaker.name)}
                      >
                        {formatSpeakerFullName(speaker.name)}
                      </span>
                      {speaker.language && (
                        <span className={cn('audio-speaker-lang')}>{langLabel(speaker.language)}</span>
                      )}
                    </div>
                    <div className={cn('audio-speaker__timeline-wrapper')}>
                      {speaker.segments.map((seg, i) => {
                        const isActive = currentTime >= seg.startSec && currentTime < seg.endSec;
                        const tooltip = `${formatSecondsShort(seg.startSec)} → ${formatSecondsShort(seg.endSec)}`;
                        return (
                          <div
                            key={i}
                            role="button"
                            tabIndex={0}
                            title={tooltip}
                            className={cn('audio-speaker__timeline-segment', { active: isActive })}
                            style={{
                              left: `${seg.startPct}%`,
                              width: `${Math.max(seg.endPct - seg.startPct, 0.5)}%`,
                              backgroundColor: speaker.color,
                            }}
                            onClick={() => seekAll(seg.startSec)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                seekAll(seg.startSec);
                              }
                            }}
                          >
                            <span className={cn('audio-speaker__timeline-tooltip')}>{tooltip}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default TranscriptionMediaPanel;
